import React from "react";
import { openWhatsApp } from "@/lib/ibloomConfig";

/**
 * Reusable WhatsApp share button. Used on catalogue tiles and product detail
 * modals so customers can forward a Mapbazar product to a friend with one tap.
 *
 * If the browser exposes the Web Share API (mobile Chrome/Safari) we use it so
 * the user can pick WhatsApp / Telegram / SMS / etc. Otherwise we fall back to
 * `https://wa.me/?text=...` which opens WhatsApp directly.
 *
 * Public link convention: prefer the canonical product page
 * `${origin}/product/{slug}`; fall back to
 * `${origin}/shop/{shop_slug}?product={product_id}` (PublicShop auto-opens that
 * product modal for the recipient). No login required.
 */
function buildShare(product) {
  const origin = (typeof window !== "undefined" && window.location?.origin) || "https://mapbazar.com";
  const shopKey = product.shop_slug_url || product.shop_id || product.shopId || "";
  const url = product.seo_path
    ? `${origin}${product.seo_path}`
    : product.slug
      ? `${origin}/product/${product.slug}`
      : shopKey
        ? `${origin}/shop/${shopKey}?product=${encodeURIComponent(product.id || "")}`
        : origin;
  const priceTxt = product.price ? `₹${product.price}` : "";
  const shopName = product.shop_name || "a local shop";
  const text = `${product.name || "Check this out"} ${priceTxt} from ${shopName} on Mapbazar — buy local, get delivered in 30 min.\n${url}`;
  return { url, text, title: product.name || "Mapbazar product" };
}

export default function ShareButton({ product, variant = "pill", testIdSuffix = "" }) {
  const onShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { url, text, title } = buildShare(product);
    // Try the native share sheet first (mobile).
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (_) {
        // user cancelled, or Web Share unavailable — fall through to wa.me
      }
    }
    // wa.me deep link — opens WhatsApp app if installed, else web. TWA-safe open.
    const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
    openWhatsApp(wa);
  };

  if (variant === "icon") {
    return (
      <button
        type="button"
        data-testid={`share-btn-icon${testIdSuffix ? `-${testIdSuffix}` : ""}`}
        onClick={onShare}
        aria-label="Share on WhatsApp"
        title="Share on WhatsApp"
        style={{
          width: 32, height: 32, borderRadius: "50%", border: "none",
          background: "rgba(255,255,255,0.94)",
          color: "#1A7F4E", fontSize: 15, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 8px rgba(15,20,30,0.18)",
        }}
      >
        📤
      </button>
    );
  }

  // "pill" — full-width button used in detail modals.
  return (
    <button
      type="button"
      data-testid={`share-btn${testIdSuffix ? `-${testIdSuffix}` : ""}`}
      onClick={onShare}
      style={{
        marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        width: "100%", padding: 12, borderRadius: 100, border: "1px solid #25D366",
        background: "white", color: "#1A7F4E", fontWeight: 700, fontSize: 13,
        cursor: "pointer",
      }}
    >
      📤 Share this product on WhatsApp
    </button>
  );
}
